import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ChevronRight, MapPin, Users, Leaf, Award, ArrowLeft } from "lucide-react";
import { farms } from "@/data/farms";
import { products } from "@/data/products";
import { ProductCard } from "@/components/product/ProductCard";

export default function FarmDetailPage() {
  const { farmId } = useParams();
  const farm = farms.find((f) => f.id === farmId);

  if (!farm) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl font-semibold text-foreground">Farm not found</h1>
          <p className="text-muted-foreground mt-2">The farm you're looking for doesn't exist.</p>
          <Button asChild className="mt-6">
            <Link to="/farm-stories">Back to Farm Stories</Link>
          </Button>
        </div>
      </div>
    );
  }

  const farmProducts = products.filter((p) => farm.productsFromFarm.includes(p.id));

  // Split the full story into paragraphs
  const storyParagraphs = farm.storyFull.split('\n\n');

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-end">
        <div className="absolute inset-0 z-0">
          <img
            src={farm.heroImage}
            alt={farm.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/farm-stories" className="hover:text-foreground transition-colors">Farm Stories</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">{farm.name}</span>
          </nav>

          <div className="flex items-center gap-1.5 text-muted-foreground mb-2">
            <MapPin className="h-4 w-4" />
            <span>{farm.location}</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground">
            {farm.name}
          </h1>

          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
            {farm.storyShort}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Story Column */}
          <div className="lg:col-span-2">
            {/* Farmer Quote */}
            <blockquote className="text-xl lg:text-2xl font-display italic text-foreground border-l-4 border-primary pl-6 mb-12">
              "{farm.farmerQuote}"
              <footer className="mt-4 text-base font-body not-italic text-muted-foreground">
                — {farm.farmerName}
              </footer>
            </blockquote>

            {/* Full Story */}
            <div className="prose prose-neutral max-w-none">
              {storyParagraphs.map((paragraph, index) => (
                <p key={index} className="text-muted-foreground leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Farm Images Gallery */}
            {farm.images.length > 1 && (
              <div className="mt-12">
                <h2 className="font-display text-2xl font-semibold text-foreground mb-6">
                  Life on the Farm
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {farm.images.slice(1).map((image, index) => (
                    <div key={index} className="aspect-[4/3] rounded-xl overflow-hidden">
                      <img
                        src={image}
                        alt={`${farm.name} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="mt-12 lg:mt-0">
            <div className="lg:sticky lg:top-24 space-y-6">
              {/* Farm Details Card */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-display text-lg font-semibold text-foreground mb-4">
                    Farm Details
                  </h3>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Location</p>
                        <p className="text-sm text-muted-foreground">{farm.location}, {farm.region}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Farmers</p>
                        <p className="text-sm text-muted-foreground">{farm.farmerName}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Leaf className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Sheep Breed</p>
                        <p className="text-sm text-muted-foreground">{farm.sheepBreed}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Award className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Flock Size</p>
                        <p className="text-sm text-muted-foreground">{farm.flockSize}</p>
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Certifications */}
                  {farm.certifications && farm.certifications.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-foreground mb-3">Certifications</p>
                      <div className="flex flex-wrap gap-2">
                        {farm.certifications.map((cert, index) => (
                          <Badge key={index} variant="outline" className="font-normal">
                            {cert}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sustainability Practices Card */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-display text-lg font-semibold text-foreground mb-4">
                    Sustainability Practices
                  </h3>
                  <ul className="space-y-3">
                    {farm.sustainabilityPractices.map((practice, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="text-success mt-1">✓</span>
                        {practice}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </aside>
        </div>
      </div>

      {/* Products from this Farm */}
      {farmProducts.length > 0 && (
        <section className="py-12 lg:py-16 bg-secondary/30 border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-foreground mb-8">
              Products from {farm.name}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {farmProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back Link */}
      <section className="py-8 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button asChild variant="ghost" className="gap-2">
            <Link to="/farm-stories">
              <ArrowLeft className="h-4 w-4" />
              Back to all Farm Stories
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
